import React from 'react';
import { FileText, CheckCircle, AlertCircle, Scale, Users, Shield } from 'lucide-react';

const TermsConditions = () => {
  return (
    <div className="bg-white text-gray-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-6">
            <FileText className="text-white" size={40} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-600">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed">
              Welcome to IlinkTrading. These Terms and Conditions outline the rules and regulations for the use of our website and services. By accessing this website and using our services, you accept these terms and conditions in full. Do not continue to use IlinkTrading if you do not agree to all of the terms and conditions stated on this page.
            </p>
          </div>

          {/* Agreement to Terms */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="text-blue-600" size={24} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Agreement to Terms</h2>
            </div>
            <div className="space-y-4 pl-4">
              <p className="text-gray-700 leading-relaxed">
                By using our website and services, you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you must not use our website or services.
              </p>
            </div>
          </div>

          {/* Services */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="text-purple-600" size={24} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Our Services</h2>
            </div>
            <div className="space-y-6 pl-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Service Description</h3>
                <p className="text-gray-700 leading-relaxed">
                  IlinkTrading provides digital agency services including web development, UI/UX design, blockchain development, mobile app development, and digital marketing services. The specific services provided will be outlined in individual project agreements.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Service Modifications</h3>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify, suspend, or discontinue any part of our services at any time without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the services.
                </p>
              </div>
            </div>
          </div>

          {/* User Obligations */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Scale className="text-green-600" size={24} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">User Obligations</h2>
            </div>
            <div className="space-y-4 pl-4">
              <p className="text-gray-700 leading-relaxed">As a user of our services, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Not use our services for any illegal or unauthorized purpose</li>
                <li>Not interfere with or disrupt our services or servers</li>
                <li>Comply with all applicable local, state, national, and international laws</li>
              </ul>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Shield className="text-orange-600" size={24} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Intellectual Property Rights</h2>
            </div>
            <div className="space-y-4 pl-4">
              <p className="text-gray-700 leading-relaxed">
                Unless otherwise stated, IlinkTrading and/or its licensors own the intellectual property rights for all material on this website and services. All intellectual property rights are reserved.
              </p>
              <p className="text-gray-700 leading-relaxed">
                For client projects, intellectual property rights will be transferred to the client upon full payment, as specified in individual project agreements.
              </p>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Payment Terms</h2>
            <div className="space-y-4 pl-4">
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Payment terms will be specified in individual project agreements</li>
                <li>All prices are in USD unless otherwise stated</li>
                <li>Payments are typically structured with an initial deposit and milestone payments</li>
                <li>Late payments may result in project suspension or termination</li>
                <li>All fees are non-refundable unless otherwise specified in writing</li>
              </ul>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="text-red-600" size={24} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Limitation of Liability</h2>
            </div>
            <div className="space-y-4 pl-4">
              <p className="text-gray-700 leading-relaxed">
                To the fullest extent permitted by applicable law, IlinkTrading shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Your use or inability to use our services</li>
                <li>Any unauthorized access to or use of our servers</li>
                <li>Any interruption or cessation of transmission to or from our services</li>
                <li>Any bugs, viruses, or other harmful code transmitted through our services</li>
                <li>Any errors or omissions in any content</li>
              </ul>
            </div>
          </div>

          {/* Warranty Disclaimer */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Warranty Disclaimer</h2>
            <p className="text-gray-700 leading-relaxed pl-4">
              Our services are provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, secure, or error-free, or that any defects will be corrected.
            </p>
          </div>

          {/* Termination */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Termination</h2>
            <p className="text-gray-700 leading-relaxed pl-4">
              We may terminate or suspend your access to our services immediately, without prior notice or liability, for any reason, including without limitation if you breach these Terms and Conditions. Upon termination, your right to use the services will immediately cease.
            </p>
          </div>

          {/* Governing Law */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Governing Law</h2>
            <p className="text-gray-700 leading-relaxed pl-4">
              These Terms shall be governed and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions. Any disputes arising from these terms will be resolved in the courts of California.
            </p>
          </div>

          {/* Changes to Terms */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed pl-4">
              We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </p>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms and Conditions, please contact us:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>Email: hello@ilinktrading.com</li>
              <li>Address: 115 S Church St, Grass Valley, CA 95945, United States</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditions;
